import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import RequestCompOffAdminActionType from './RequestCompOffAdmin.types';
import {
	requestCompOffAdminAPI,
	getAllEmployeesCompOffAdminAPI,
	getHolidaysCompOffAdminAPI,
} from './RequestCompOffAdmin.services';
import {
	requestCompOffAdminFailure,
	requestCompOffAdminSuccess,
	getAllEmployeesCompOffAdminSuccess,
	getAllEmployeesCompOffAdminFailure,
	getHolidaysCompOffAdminSuccess,
	getHolidaysCompOffAdminFailure,
} from './RequestCompOffAdmin.action';
import AlertManager from '../../utils/alertManager';
import { encryptData } from '../_common/enode-decode';

interface RequestCompOffAdminData {
	employeeID: string;
	date: string;
	reason: string;
	organisationID: number;
}

interface OrganisationData {
	organisationID: number;
}

export function* requestCompOffAdmin({
	payload,
}: {
	payload: RequestCompOffAdminData;
}): Generator<any, void, any> {
	try {
		if (process.env.NODE_ENV === 'development') console.log('Request Comp Off Admin saga started with payload:', payload);
		const encryptedPayload = encryptData(
			payload,
			RequestCompOffAdminActionType.REQUEST_COMPOFF_ADMIN_KEY,
		);
		const response = yield call(requestCompOffAdminAPI as any, encryptedPayload);
		const responseData = response?.data;

		if (responseData?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}

		if (responseData?.status === 'error') {
			yield put(requestCompOffAdminFailure(responseData));
		} else {
			yield put(requestCompOffAdminSuccess(responseData));
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in requestCompOffAdmin saga:', error);
		if (error?.response?.data?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}
		yield put(requestCompOffAdminFailure(error?.response?.data || error));
	}
}

export function* getAllEmployeesCompOffAdmin({
	payload,
}: {
	payload: OrganisationData;
}): Generator<any, void, any> {
	try {
		if (process.env.NODE_ENV === 'development') console.log('Get All Employees Comp Off Admin saga started with payload:', payload);
		const encryptedPayload = encryptData(
			payload,
			RequestCompOffAdminActionType.GET_ALL_EMPLOYEES_COMPOFF_ADMIN_KEY,
		);
		const response = yield call(getAllEmployeesCompOffAdminAPI as any, encryptedPayload);
		const responseData = response?.data;

		if (responseData?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}

		if (!responseData || !responseData.response) {
			if (process.env.NODE_ENV === 'development') console.error('Invalid response structure:', responseData);
			yield put(getAllEmployeesCompOffAdminFailure({
				status: 'error',
				message_text: 'Invalid response from server. Please try again.'
			}));
			return;
		}

		if (responseData?.status === 'error') {
			yield put(getAllEmployeesCompOffAdminFailure(responseData));
		} else {
			yield put(getAllEmployeesCompOffAdminSuccess(responseData));
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getAllEmployeesCompOffAdmin saga:', error);
		if (error?.response?.status === 401 || error?.response?.data?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}
		if (error?.response?.status === 405) {
			yield put(getAllEmployeesCompOffAdminFailure({
				status: 'error',
				message_text: 'Method not allowed. Please contact administrator.'
			}));
		} else if (error?.response?.data) {
			yield put(getAllEmployeesCompOffAdminFailure(error.response.data));
		} else {
			yield put(getAllEmployeesCompOffAdminFailure({
				status: 'error',
				message_text: error?.message || 'Network error. Please try again.'
			}));
		}
	}
}

export function* getHolidaysCompOffAdmin(): Generator<any, void, any> {
	try {
		if (process.env.NODE_ENV === 'development') console.log('Get Holidays Comp Off Admin saga started');
		const response = yield call(getHolidaysCompOffAdminAPI as any);
		const responseData = response?.data;

		if (responseData?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}

		if (responseData?.status === 'error') {
			yield put(getHolidaysCompOffAdminFailure(responseData));
		} else {
			yield put(getHolidaysCompOffAdminSuccess(responseData));
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getHolidaysCompOffAdmin saga:', error);
		if (error?.response?.status === 401 || error?.response?.data?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}
		if (error?.response?.data) {
			yield put(getHolidaysCompOffAdminFailure(error.response.data));
		} else {
			yield put(getHolidaysCompOffAdminFailure({
				status: 'error',
				message_text: error?.message || 'Network error. Please try again.'
			}));
		}
	}
}

export function* onRequestCompOffAdminStart(): Generator<any, void, any> {
	yield takeLatest(RequestCompOffAdminActionType.REQUEST_COMPOFF_ADMIN_START as any, requestCompOffAdmin);
}

export function* onGetAllEmployeesCompOffAdminStart(): Generator<any, void, any> {
	yield takeLatest(
		RequestCompOffAdminActionType.GET_ALL_EMPLOYEES_COMPOFF_ADMIN_START as any,
		getAllEmployeesCompOffAdmin,
	);
}

export function* onGetHolidaysCompOffAdminStart(): Generator<any, void, any> {
	yield takeLatest(
		RequestCompOffAdminActionType.GET_HOLIDAYS_COMPOFF_ADMIN_START as any,
		getHolidaysCompOffAdmin,
	);
}

export function* requestCompOffAdminSaga(): Generator<any, void, any> {
	yield all([
		fork(onRequestCompOffAdminStart),
		fork(onGetAllEmployeesCompOffAdminStart),
		fork(onGetHolidaysCompOffAdminStart),
	]);
}

